import React from "react";

const initialState = {
  name: "",
  email: "",
  phone: "",
  contactType: "",
  credits: 0,
  token: undefined as string | undefined,
  authType: null as string | null,
};

const userContext = React.createContext({
  ...initialState,
  onUserUpdate: (_type: string, _value: any) => {},
  setCredentials: (_token: string, _credits: number, _authType?: string | null) => {},
  clearCredentials: () => {},
});

export const UserProvider = ({ children }) => {
  const value = useUserValue();
  return <userContext.Provider value={value}>{children}</userContext.Provider>;
};

export const useUser = () => {
  return React.useContext(userContext);
};

const userReducer = (
  state: typeof initialState,
  action: {
    type: string;
    payload: any;
  }
): typeof initialState => {
  switch (action.type) {
    case "email":
      return {
        ...state,
        email: action.payload,
      };
    case "phone":
      return {
        ...state,
        phone: action.payload,
      };
    case "name":
      return {
        ...state,
        name: action.payload,
      };
    case "contact":
      return {
        ...state,
        contactType: action.payload,
      };
    case "setCredentials":
      return {
        ...state,
        token: action.payload.token,
        credits: action.payload.credits,
        authType: action.payload.authType || null,
      };
    case "clearCredentials":
      return {
        ...state,
        token: undefined,
        credits: 0,
        authType: null,
      };
    default:
      throw new Error("Invalid action type");
  }
};

const useUserValue = (): {
  name: string;
  email: string;
  phone: string;
  contactType: string;
  credits: number;
  token: string | undefined;
  authType: string | null;
  onUserUpdate: (type: string, payload: any) => void;
  setCredentials: (token: string, credits: number, authType?: string | null) => void;
  clearCredentials: () => void;
} => {
  const [state, dispatch] = React.useReducer(userReducer, initialState);

  const onUserUpdate = React.useCallback(
    (type: string, value) => {
      dispatch({ type, payload: value });
    },
    [dispatch]
  );

  const setCredentials = React.useCallback(
    (token: string, credits: number, authType?: string | null) => {
      dispatch({ type: "setCredentials", payload: { token, credits, authType } });
    },
    [dispatch]
  );

  const clearCredentials = React.useCallback(() => {
    dispatch({ type: "clearCredentials", payload: null });
  }, [dispatch]);

  const { name, contactType, email, phone, credits, token, authType } = state;

  return {
    name,
    contactType,
    email,
    phone,
    credits,
    token,
    authType,
    onUserUpdate,
    setCredentials,
    clearCredentials,
  };
};
